import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import { FaSave, FaArrowLeft, FaUserEdit } from "react-icons/fa";

const getAuthToken = () => localStorage.getItem("authToken");

const StudentEdit = () => {
    const { id } = useParams();
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        fetchBatches();
        fetchStudentDetails();
    }, []);


    const fetchBatches = async () => {
        const token = getAuthToken();
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }

        try {
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}get-batches`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status === "success") {
                setBatches(response.data.data);
            } else {
                alert("Failed to load batches.");
            }
        } catch (err) {
            alert("Error fetching batches.");
        }
    };

    const fetchStudentDetails = async () => {
        const token = getAuthToken();
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }

        try {
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}get-student`,
                { id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status === "success") {
                setFormData(response.data.data);
                if (response.data.data.photo_url) {
                    setPhotoPreview(response.data.data.photo_url);
                }
            } else {
                alert("Failed to fetch student details.");
                navigate(-1);
            }
        } catch (err) {
            alert("Error fetching student details.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhoto(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = getAuthToken();
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }

        try {
            setLoading(true);
            const formDataToSend = new FormData();
            Object.keys(formData).forEach((key) => {
                formDataToSend.append(key, formData[key]);
            });

            if (photo) {
                formDataToSend.append("photo", photo); // Send new photo
            } else if (formData.photo === "") {
                formDataToSend.append("delete_photo", true); // Send flag to delete photo
            }
            

            const response = await axios.post(
                `${appConstants.API_ROOT_URL}update-student`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.status === "success") {
                //alert("Student updated successfully!");
                navigate(-1);
            } else {
                alert(response.data.message || "Failed to update student.");
            }
        } catch (err) {
            alert(err.response?.data?.message || "Error updating student.");
        } finally {
            setLoading(false);
        }
    };

    if (!formData) return <Layout><p>Loading...</p></Layout>;

    return (
        <Layout>
            <div className="container mt-4">
                
                {/* Title & Buttons Section */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <FaUserEdit size={28} className="me-2 text-primary" />
                        <h3 className="m-0">Edit Student</h3>
                    </div>

                    <div className="d-flex">
                        <button className="btn btn-secondary d-flex align-items-center me-2" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-2" /> Back
                        </button>

                        <button type="submit" className="btn btn-success d-flex align-items-center" onClick={handleSubmit} disabled={loading}>
                            <FaSave className="me-2" /> {loading ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </div>

                <div className="card p-4 shadow">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Date of Application</label>
                                    <input type="date" className="form-control" name="date_of_application" value={formData.date_of_application} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">ID Card No</label>
                                    <input type="text" className="form-control" name="id_card_no" value={formData.id_card_no} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Gender</label>
                                    <select className="form-control" name="gender" value={formData.gender} onChange={handleChange}>
                                        <option value="">Select Gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Date of Birth</label>
                                    <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Parents</label>
                                    <textarea className="form-control" name="parents" value={formData.parents} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Mobile</label>
                                    <input type="text" className="form-control" name="mobile" value={formData.mobile} onChange={handleChange} />
                                </div>
                            </div>
    
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Upload Photo</label>
                                    <input 
                                        type="file" 
                                        className="form-control" 
                                        accept="image/*" 
                                        ref={fileInputRef} 
                                        onChange={handlePhotoChange} 
                                    />
                                    {photoPreview || formData.photo ? (
                                        <div className="mt-3">
                                            <img
                                                src={photoPreview || (formData.photo ? `${appConstants.API_ROOT_URL}uploads/students/${formData.photo}` : '')}
                                                alt="Preview"
                                                className="img-thumbnail"
                                                width="150"
                                            />
                                            <button 
                                                type="button" 
                                                className="btn btn-danger btn-sm d-block mt-2"
                                                onClick={() => {
                                                    setPhoto(null);
                                                    setPhotoPreview(null);
                                                    setFormData({ ...formData, photo: "" });
                                                    if (fileInputRef.current) {
                                                        fileInputRef.current.value = "";
                                                    }
                                                }}
                                            >
                                                Remove Photo
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Batch</label>
                                    <select className="form-control" name="batch" value={formData.batch} onChange={handleChange}>
                                        <option value="">Select Batch</option>
                                        {batches.map((batch) => (
                                            <option key={batch.id} value={batch.id}>
                                                {batch.class_name} {batch.division}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Address</label>
                                    <textarea className="form-control" name="address" value={formData.address} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Remark</label>
                                    <textarea className="form-control" name="remark" value={formData.remark} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Parent Access Code</label>
                                    <input type="text" className="form-control" name="parent_access_code" value={formData.parent_access_code} readOnly />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Status</label>
                                    <select className="form-control" name="status" value={formData.status} onChange={handleChange}>
                                        <option value="Active">Active</option>
                                        <option value="Suspend">Suspend</option>
                                    </select>
                                </div>
                            </div>
                        </div>
    
                    </form>
                </div>
            </div>
        </Layout>
    );
    
        
};

export default StudentEdit;
